import { Widget, WidgetId } from '../hooks/useDashboardWidgets'
import { useLanguage } from '../hooks/useLanguage'
import {
  Eye,
  EyeOff,
  ChevronUp,
  ChevronDown,
  X,
  RotateCcw,
} from 'lucide-react'

interface WidgetConfigPanelProps {
  widgets: Widget[]
  onToggle: (id: WidgetId) => void
  onMove: (id: WidgetId, direction: 'up' | 'down') => void
  onReset: () => void
  onClose: () => void
}

export default function WidgetConfigPanel({
  widgets,
  onToggle,
  onMove,
  onReset,
  onClose,
}: WidgetConfigPanelProps) {
  const { language } = useLanguage()

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-fadeIn">
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-modal w-full max-w-md overflow-hidden animate-scaleIn">
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
          <h2 className="text-xl font-bold text-slate-900 dark:text-white">
            {language === 'it' ? 'Configura Widget' : 'Configure Widgets'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
          >
            <X className="w-5 h-5 text-slate-500 dark:text-slate-400" />
          </button>
        </div>

        {/* Widget List */}
        <div className="p-4 max-h-[60vh] overflow-y-auto">
          <div className="space-y-2">
            {widgets.map((widget, index) => (
              <div
                key={widget.id}
                className={`flex items-center gap-3 p-3 rounded-xl border transition-all ${
                  widget.visible
                    ? 'bg-white dark:bg-slate-700 border-slate-200 dark:border-slate-600'
                    : 'bg-slate-50 dark:bg-slate-800/50 border-slate-100 dark:border-slate-700/50 opacity-60'
                }`}
              >
                {/* Visibility Toggle */}
                <button
                  onClick={() => onToggle(widget.id)}
                  className={`p-2 rounded-lg transition-colors ${
                    widget.visible
                      ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400'
                      : 'bg-slate-100 dark:bg-slate-700 text-slate-400 dark:text-slate-500'
                  }`}
                  title={widget.visible ? 
                    (language === 'it' ? 'Nascondi widget' : 'Hide widget') :
                    (language === 'it' ? 'Mostra widget' : 'Show widget')
                  }
                >
                  {widget.visible ? (
                    <Eye className="w-5 h-5" />
                  ) : (
                    <EyeOff className="w-5 h-5" />
                  )}
                </button>

                {/* Widget Name */}
                <span className={`flex-1 font-medium ${
                  widget.visible
                    ? 'text-slate-900 dark:text-white'
                    : 'text-slate-500 dark:text-slate-400'
                }`}>
                  {language === 'it' ? widget.name : widget.nameEn}
                </span>

                {/* Order Controls */}
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => onMove(widget.id, 'up')}
                    disabled={index === 0}
                    className={`p-1.5 rounded-lg transition-colors ${
                      index === 0
                        ? 'text-slate-300 dark:text-slate-600 cursor-not-allowed'
                        : 'hover:bg-slate-100 dark:hover:bg-slate-600 text-slate-500 dark:text-slate-400'
                    }`}
                    title={language === 'it' ? 'Sposta su' : 'Move up'}
                  >
                    <ChevronUp className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => onMove(widget.id, 'down')}
                    disabled={index === widgets.length - 1}
                    className={`p-1.5 rounded-lg transition-colors ${
                      index === widgets.length - 1
                        ? 'text-slate-300 dark:text-slate-600 cursor-not-allowed'
                        : 'hover:bg-slate-100 dark:hover:bg-slate-600 text-slate-500 dark:text-slate-400'
                    }`}
                    title={language === 'it' ? 'Sposta giù' : 'Move down'}
                  >
                    <ChevronDown className="w-5 h-5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-200 dark:border-slate-700 flex items-center justify-between">
          <button
            onClick={onReset}
            className="flex items-center gap-2 px-4 py-2 text-sm text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white transition-colors"
          >
            <RotateCcw className="w-4 h-4" />
            {language === 'it' ? 'Ripristina default' : 'Reset to default'}
          </button>
          <button
            onClick={onClose}
            className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-xl transition-colors"
          >
            {language === 'it' ? 'Fatto' : 'Done'}
          </button>
        </div>
      </div>
    </div>
  )
}
